import rest from '@modules/rest'
import { PERSONAL_AREA } from '@content/consts/Personal'
import { getEmployeeInfoSchema } from '@services/systemField'
import * as get from '@graphql/middleware/queries'
import middleware from '@graphql/middleware'
import * as mappers from '@convenia/common-organisms/SystemFields/content/mappers/personal'

const sortNationalities = nationalities =>
  (nationalities || []).sort((a, b) => {
    const first = a.name.toLowerCase()
    const second = b.name.toLowerCase()

    return (first < second) ? -1 : (first > second) ? 1 : 0
  })

export async function getOptions () {
  const [ err, { employeeOptions: data = {} } ] = await middleware(get.GetPersonalOptions)

  const options = {
    personal: {
      visaTypes: data.visaTypes,
      nationalities: sortNationalities(data.nationalities),
      ethnicities: data.ethnicities,
      genders: data.genders,
      genderIdentities: data.genderIdentities,
      civilStatus: data.maritalStatuses,
      residenceTimes: data.residenceTimes,
      entryConditions: data.entryConditions,
      addressDescription: data.addressDescription
    },
    handicap: {
      disabilities: data.disabilities
    },
  }

  return [ err, options ]
}

export const getPersonal = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/personal-information/employee`

    const { data } = await rest.get(url)

    const mappedData = mappers.mapPersonal(data)

    return [ null, mappedData ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updatePersonal = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/personal-information/employee`
    const body = mappers.mapPersonalInput({ data, schema })

    const response = await rest.post(url, { body })

    const personal = mappers.mapPersonal(response?.data || {})

    return [ null, personal ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updateNationalities = async (data, params) => {
  try {
    const { employeeId, companyId } = params
    const { nationalities } = data
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/nationalities/employee`

    const response = await rest.post(url, { body: { nationalities } })

    return [ null, response?.data ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updateEmployee = async (data, params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/employee`
    const body = mappers.mapEmployeeInput({ data })

    await rest.put(url, { body })

    return [ null, true ]
  } catch (err) {
    return [ err, null ]
  }
}

export const getForeign = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/foreign/employee`

    const { data } = await rest.get(url)

    const mappedData = mappers.mapForeign(data)

    return [ null, mappedData ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updateForeign = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/foreign/employee`
    const body = mappers.mapForeignInput({ data, schema })

    const response = await rest.post(url, { body })

    const foreign = mappers.mapForeign(response?.data || {})

    return [ null, foreign ]
  } catch (err) {
    return [ err, null ]
  }
}

export const getHandicap = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/handicapped/employee`

    const { data } = await rest.get(url)

    const handicap = mappers.mapHandicap(data)

    return [ null, handicap ]
  } catch (err) {
    return [ err, null ]
  }
}

export const updateHandicap = async (data, params) => {
  try {
    const { employeeId, companyId, schema } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/handicapped/employee`
    const body = mappers.mapHandicapInput({
      data,
      schema,
      sectionId: PERSONAL_AREA.HANDICAP,
    })

    const response = await rest.post(url, { body })

    const handicap = mappers.mapHandicap(response?.data || {})

    return [ null, handicap ]
  } catch (err) {
    return [ err, null ]
  }
}

export const deleteHandicap = async (params) => {
  try {
    const { employeeId, companyId } = params
    const url = `/employees/proxy/companies/${companyId}/employees/${employeeId}/handicapped/employee`

    await rest.delete(url)

    return [ null, true ]
  } catch (err) {
    return [ err, null ]
  }
}

export const mapSection = data => ({
  schema: data?.schema || {},
  values: data?.values || {},
})

export const mapCustomFields = data => ({
  personal: mapSection(data.find(({ id }) => id === PERSONAL_AREA.PERSONAL)),
  foreign: mapSection(data.find(({ id }) => id === PERSONAL_AREA.FOREIGN)),
  bankAccounts: mapSection(data.find(({ id }) => id === PERSONAL_AREA.BANK_ACCOUNTS)),
  educations: mapSection(data.find(({ id }) => id === PERSONAL_AREA.EDUCATIONS)),
  handicap: mapSection(data.find(({ id }) => id === PERSONAL_AREA.HANDICAP)),
})

export const getCustomFields = async (getters) => {
  const employeeId = getters.getEmployeeUuid
  const companyId = getters.getCompanyUuid

  const [ err, fields ] = await getEmployeeInfoSchema({
    employeeId,
    companyId,
    area: PERSONAL_AREA.LABEL
  })

  return [ err, mapCustomFields(fields) ]
}
